import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Trash2,
  MapPin, ExternalLink, Package, X, Calculator,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from '@/components/proepoxy/productData';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function MetallicStep6({ state, priority, setPriority, currentStep, goToStep, navigate, onExit }) {
  const {
    primerAreas, setPrimerAreas, designCoatSelections, pigmentSelections, topCoatSelections,
    steps,
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    addMetallicPowder, updateMetallicPowderQty,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    calculateTotal, getItemCount,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Protective Top Coats</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calculator className="w-4 h-4" />
                      Areas from Step 1
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Select your protective top coat for each area. Your m² values have been carried forward.
                  </p>

                  {primerAreas.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No areas defined. Please go back to Step 1 to add areas.</p>
                      <Button
                        onClick={() => goToStep(1)}
                        variant="outline"
                        className="border-2 border-[#7A0B7A] text-[#7A0B7A]"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Define Areas
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {primerAreas.map((area, index) => (
                        <div
                          key={area.id}
                          className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#7A0B7A]/30 transition-colors"
                          data-testid={`topcoat-area-${index}`}
                        >
                          {/* Area Header */}
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                            <div>
                              <h3 className="font-bold text-[#27292E]">{area.name}</h3>
                              <p className="text-sm text-gray-500">{area.m2}m²</p>
                            </div>
                            <span className="px-3 py-1 bg-[#7A0B7A]/10 text-[#7A0B7A] rounded-full text-sm font-medium">
                              {area.m2}m²
                            </span>
                          </div>

                          {/* Top Coat Type Selection */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Top Coat Type</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <button
                                onClick={() => initAreaTopCoat(area.id, 'polyurethane')}
                                className={`p-3 rounded-xl text-left transition-all border-2 ${
                                  area.topCoatType === 'polyurethane'
                                    ? 'border-[#7A0B7A] bg-[#7A0B7A]/5'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                data-testid={`area-${index}-polyurethane`}
                              >
                                <h4 className="font-medium text-[#27292E]">Polyurethane</h4>
                                <p className="text-xs text-gray-500">Gloss, Satin & Matt</p>
                              </button>
                              <button
                                onClick={() => initAreaTopCoat(area.id, 'polyaspartic')}
                                className={`p-3 rounded-xl text-left transition-all border-2 ${
                                  area.topCoatType === 'polyaspartic'
                                    ? 'border-[#7A0B7A] bg-[#7A0B7A]/5'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                data-testid={`area-${index}-polyaspartic`}
                              >
                                <h4 className="font-medium text-[#27292E]">Polyaspartic</h4>
                                <p className="text-xs text-gray-500">Gloss only</p>
                              </button>
                            </div>
                          </div>

                          {/* Finish & Packs Selection */}
                          {area.topCoatType && (
                            <div className="bg-gray-50 rounded-xl p-4">
                              {/* Finish Selection */}
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Finish</label>
                                <div className="flex gap-2 flex-wrap">
                                  {TOP_COAT_PRODUCTS[area.topCoatType].finishes.map(finish => (
                                    <button
                                      key={finish}
                                      onClick={() => updateAreaTopCoatFinish(area.id, finish)}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        area.topCoatFinish === finish
                                          ? 'bg-[#7A0B7A] text-white'
                                          : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                                      }`}
                                      data-testid={`area-${index}-finish-${finish.toLowerCase()}`}
                                    >
                                      {finish}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Recommended Packs */}
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-medium text-[#27292E]">Recommended Packs</h4>
                                  <span className="text-xs text-gray-500">Adjust quantities if needed</span>
                                </div>
                                <div className="space-y-2">
                                  {TOP_COAT_PRODUCTS[area.topCoatType].packs.map(packOption => {
                                    const areaPack = (area.topCoatPacks || []).find(p => p.size === packOption.size);
                                    const qty = areaPack ? areaPack.quantity : 0;
                                    return (
                                      <div
                                        key={packOption.size}
                                        className={`p-4 rounded-lg ${
                                          qty > 0 ? 'bg-white border-2 border-[#7A0B7A]/20' : 'bg-white/50'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-medium text-[#27292E]">{packOption.size}m² pack</span>
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => updateAreaTopCoatPackQty(area.id, packOption.size, -1)}
                                              className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                              data-testid={`area-${index}-topcoat-pack-${packOption.size}-minus`}
                                            >
                                              <Minus className="w-4 h-4" />
                                            </button>
                                            <span className="w-8 text-center font-bold text-lg">{qty}</span>
                                            <button
                                              onClick={() => updateAreaTopCoatPackQty(area.id, packOption.size, 1)}
                                              className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white"
                                              data-testid={`area-${index}-topcoat-pack-${packOption.size}-plus`}
                                            >
                                              <Plus className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                          <span className="text-gray-500">£{packOption.price.toFixed(2)} each</span>
                                          {qty > 0 && (
                                            <span className="font-bold text-[#7A0B7A]">
                                              Total: £{(qty * packOption.price).toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* Area Subtotal */}
                                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between">
                                  <span className="text-gray-600">
                                    {area.name} subtotal ({TOP_COAT_PRODUCTS[area.topCoatType].name} - {area.topCoatFinish})
                                  </span>
                                  <span className="font-bold text-[#7A0B7A]">£{getAreaTopCoatSubtotal(area).toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getItemCount()} item{getItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                    </div>
                    <p className="text-3xl font-bold">
                      £{calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => goToStep(5)}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(7)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl"
                  >
                    Review Order
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
